import { PartialRecord } from '../../type-helpers.model';

export enum ActivityType {
  Residential = 'residential',
  Stairwells = 'stairwells',
  // LaundryRoom = 'laundry_room',
  // BikeStorage = 'bike_storage',
  Storage = 'storage',
  Utilities = 'utilities',
  Innerwalls = 'innerwalls',
  Garage = 'garage',
  Other = 'other',

  /**
   * When no activity type is set
   */
  Unset = 'unset',

  Industry = 'industry',
  Office = 'office',
  // Warehouse = 'warehouse',
  // Parking = 'parking',
  Retail = 'retail',
  // Restaurant = 'restaurant',
  School = 'school',
  // Hospital = 'hospital',
  // Hotel = 'hotel',

  Facades = 'facades',
  Installations = 'installations',

  /**
   * Elevators currently included in the stairwells activity. Need migration and some UX to work
   */
  // Elevators = 'elevators',
}

export const activityTypes = Object.values(ActivityType) as ActivityType[];

/**
 * Use this to ensure that a record has entries for all ActivityType values.
 */
export type EnsureActivityMap<T extends Record<ActivityType, any>> = T;

/**
 * Special treated activity types where area counts as part of the program area (LOA/BOA. area that can be charged for).
 */
export const programActivityTypes = [
  // ActivityType.Hospital,
  // ActivityType.Hotel,
  ActivityType.Industry,
  ActivityType.Office,
  // ActivityType.Parking,
  ActivityType.Residential,
  // ActivityType.Restaurant,
  ActivityType.Retail,
  ActivityType.School,
  ActivityType.Garage,
] as const;

/**
 * Special treated activity types where area counts as part of the program area (LOA/BOA. area that can be charged for).
 */
export type ProgramActivityType = (typeof programActivityTypes)[number];

/**
 * Type of non-program activity types.
 */
export type OtherActivityType = Exclude<ActivityType, ProgramActivityType>;

/**
 * List of non-program activity types.
 */
export const otherActivityTypes = activityTypes.filter(
  (type) => !programActivityTypes.includes(type as ProgramActivityType),
);

/**
 * List of activity types that have a calculated area (doesn't have a share).
 */
export const calculatedAreaActivityTypes = [
  ActivityType.Facades,
  ActivityType.Installations,
  ActivityType.Stairwells,
  ActivityType.Innerwalls,
] as const;

/**
 * List of activity types that have a calculated area (doesn't have a share).
 */
export type CalculatedAreaActivityType =
  (typeof calculatedAreaActivityTypes)[number];

const ACTIVITY_COLOR_OTHER = '#D9D9D9';

const ACTIVITY_COLOR_COMMERCIAL = '#D4B9BD';

const ACTIVITY_COLOR_OFFICE = '#EED7CC';

const ACTIVITY_COLOR_RESIDENTIAL = '#C9DFD8';

const ACTIVITY_COLOR_UNSET = '#FFFFFF';

/**
 * Color for each activity type.
 */
export const activityTypeColorRecord: EnsureActivityMap<
  Record<ActivityType, string>
> = {
  // [ActivityType.BikeStorage]: ACTIVITY_COLOR_OTHER,
  [ActivityType.Facades]: ACTIVITY_COLOR_OTHER,
  [ActivityType.Garage]: ACTIVITY_COLOR_OTHER,
  // [ActivityType.Hospital]: ACTIVITY_COLOR_COMMERCIAL,
  // [ActivityType.Hotel]: ACTIVITY_COLOR_COMMERCIAL,
  [ActivityType.Industry]: ACTIVITY_COLOR_COMMERCIAL,
  [ActivityType.Installations]: ACTIVITY_COLOR_OTHER,
  [ActivityType.Innerwalls]: ACTIVITY_COLOR_OTHER,
  // [ActivityType.LaundryRoom]: ACTIVITY_COLOR_OTHER,
  [ActivityType.Office]: ACTIVITY_COLOR_OFFICE,
  [ActivityType.Other]: ACTIVITY_COLOR_OTHER,
  // [ActivityType.Parking]: ACTIVITY_COLOR_COMMERCIAL,
  [ActivityType.Residential]: ACTIVITY_COLOR_RESIDENTIAL,
  // [ActivityType.Restaurant]: ACTIVITY_COLOR_COMMERCIAL,
  [ActivityType.Retail]: ACTIVITY_COLOR_COMMERCIAL,
  [ActivityType.School]: ACTIVITY_COLOR_COMMERCIAL,
  [ActivityType.Stairwells]: ACTIVITY_COLOR_OTHER,
  [ActivityType.Storage]: ACTIVITY_COLOR_OTHER,
  [ActivityType.Unset]: ACTIVITY_COLOR_UNSET,
  [ActivityType.Utilities]: ACTIVITY_COLOR_OTHER,
  // [ActivityType.Warehouse]: ACTIVITY_COLOR_COMMERCIAL,
};

/**
 * How much m2 of each activity type is in the building / storey
 */
export type ActivityMix = PartialRecord<ActivityType, number>;

/** Program allocation used by the project list, including remaining Other area. */
export type ProgramGfaArea = { type: ActivityType; area: number };
